import { useState } from "react";
import { toast } from "sonner";
import { Search, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import PacManLoader from "@/pages/worth/PacManLoader";

const usd = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function EbayPart({ e }) {
  if (e.error) return <p className="text-[11px] text-rose-600">{e.error}</p>;
  return (
    <div data-testid="record-price-ebay">
      <p className="text-[11px] text-slate-600">
        <b data-testid="record-price-ebay-count">{e.count}</b> live eBay listing{e.count === 1 ? "" : "s"} matching {e.matched_on}
        {e.reissues_excluded > 0 && <span data-testid="record-price-ebay-excluded"> · {e.reissues_excluded} reissue{e.reissues_excluded === 1 ? "" : "s"} left out</span>}
      </p>
      {e.count > 0 && (
        <div className="mt-1 grid grid-cols-3 gap-1 text-center font-mono text-[11px]">
          {[["Low", e.low], ["Median", e.median], ["High", e.high]].map(([k, v]) => (
            <div key={k} className="rounded bg-white px-1 py-1"><span className="block text-[9px] uppercase text-slate-400">{k}</span><span data-testid={`record-price-ebay-${k.toLowerCase()}`}>{usd(v)}</span></div>
          ))}
        </div>
      )}
      <ul className="mt-1 space-y-0.5">
        {e.items?.slice(0, 3).map((it, i) => (
          <li key={i}><a href={it.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[11px] text-[#0053DA] hover:underline" data-testid={`record-price-ebay-item-${i}`}>
            <span className="shrink-0 font-mono">{usd(it.price)}</span><span className="min-w-0 truncate">{it.title}</span><ExternalLink className="h-3 w-3 shrink-0" />
          </a></li>
        ))}
      </ul>
    </div>
  );
}

function DiscogsPart({ d, onPick, picking }) {
  if (!d.connected) return <p className="text-[11px] text-slate-500" data-testid="record-price-discogs-off">Discogs: Not connected</p>;
  const r = d.release;
  if (!r) return <p className="text-[11px] text-slate-500" data-testid="record-price-discogs-none">Discogs: no matching release found</p>;
  return (
    <div data-testid="record-price-discogs">
      <a href={r.url} target="_blank" rel="noreferrer" className="block truncate text-[11px] font-semibold text-slate-900 hover:underline">{r.title}</a>
      <p className="truncate font-mono text-[10px] text-slate-500">{[r.label, r.catno, r.country, r.year, r.format].filter(Boolean).join(" · ")}</p>
      <p className="mt-0.5 text-[11px] text-slate-700">
        Lowest on Discogs <b data-testid="record-price-discogs-low">{usd(r.lowest_price)}</b> · {r.num_for_sale} for sale
        {r.suggested ? <> · suggested for your grade <b data-testid="record-price-discogs-suggested">{usd(r.suggested)}</b></> : null}
      </p>
      {d.candidates?.length > 1 && (
        <select value={r.id} disabled={picking} onChange={(e) => onPick(Number(e.target.value))} data-testid="record-price-discogs-pick"
          className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-[11px]">
          {d.candidates.map((c) => <option key={c.id} value={c.id}>{[c.catno, c.country, c.year, c.format].filter(Boolean).join(" · ") || c.title}</option>)}
        </select>
      )}
    </div>
  );
}

export default function RecordPriceCheck({ draft, patch }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const rp = draft.record_price?.checked_at ? draft.record_price : null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/drafts/${draft.id}/record-price-check`);
      patch({ record_price: data });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Save the draft first, then check prices" : e?.response?.data?.detail || "Price check failed");
    } finally {
      setBusy(false);
    }
  };
  const pick = async (id) => {
    setPicking(true);
    try {
      const { data } = await api.post(`/drafts/${draft.id}/record-price-check/release/${id}`);
      patch({ record_price: data });
    } catch {
      toast.error("Couldn't load that pressing");
    } finally {
      setPicking(false);
    }
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-100/60 p-2" data-testid="record-price-check">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-[11px] font-semibold text-slate-800">Pressing price check</p>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} className="h-7 rounded-full px-2.5 text-[11px]" data-testid="record-price-check-btn">
          {busy ? <span className="mr-1"><PacManLoader size={11} testid="record-price-pacman" /></span> : <Search className="mr-1 h-3 w-3" />}{rp ? "Re-check" : "Check prices"}
        </Button>
      </div>
      {rp && (
        <div className="mt-2 space-y-2">
          <EbayPart e={rp.ebay} />
          <DiscogsPart d={rp.discogs} onPick={pick} picking={picking} />
          <p className="text-[10px] text-slate-400">Live asking prices, not sold prices · checked {new Date(rp.checked_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
